import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import {
  type ComponentMemberEntityHandle,
  type ComponentMemberEntityHandleReassignable,
  type ComponentMemberEntityType,
  type ComponentOwnedMemberEntityHandleReassignable,
  type LevelHostedEntityType,
  componentMemberEntityTypes,
  componentOwnedMemberEntityTypes,
  componentSelectableLevelHostedEntityTypes,
  isComponentAssociatedEntityHandle,
  isComponentMemberTypeReassignable,
  isComponentOwnedMemberEntityHandle,
  levelHostedEntityTypes,
} from "building-types/handles/docEntityHandleTypes";
import type {
  ComponentHandle,
  DocEntityHandle,
  LevelHostedEntityHandle,
} from "building-types/handles/docEntityHandleTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { docGetComponentIdsForAssociatedEntity } from "../options/componentAssociations";
import { docEntityHandleHash } from "./core/handleHash";
import docEntityGetParent from "./docEntityGetParent";
import { collectUniqueChildTypes } from "./docEntityRelationTools";

const componentOwnedMemberEntityTypesReassignable = componentOwnedMemberEntityTypes.filter(
  (t): t is ComponentOwnedMemberEntityHandleReassignable["type"] => isComponentMemberTypeReassignable(t),
);

/** Entity types allowed when selecting owned entities to add to a component. */
export const componentMemberEntityTypesReassignableWithChildTypes: DocEntityType[] = collectUniqueChildTypes(
  componentOwnedMemberEntityTypesReassignable,
  { includeRootTypes: true },
);

/** Entity types selectable on the canvas for component create/add commands. */
export const componentCommandSelectableEntityTypes: DocEntityType[] = [
  ...componentMemberEntityTypesReassignableWithChildTypes,
  ...componentSelectableLevelHostedEntityTypes,
];

function isLevelHostedEntityType(type: DocEntityType): type is LevelHostedEntityType {
  return levelHostedEntityTypes.includes(type as LevelHostedEntityType);
}

/**
 * Check if a handle is a component member entity that may be reassigned to another component
 */
export function isComponentMemberEntityHandleReassignable(
  handle: DocEntityHandle | null | undefined,
): handle is ComponentMemberEntityHandleReassignable {
  return isComponentMemberEntityHandle(handle) && isComponentMemberTypeReassignable(handle.type);
}

/**
 * Check if a handle is an owned component member that may be reassigned to another component.
 */
export function isComponentOwnedMemberEntityHandleReassignable(
  handle: DocEntityHandle | null | undefined,
): handle is ComponentOwnedMemberEntityHandleReassignable {
  if (!handle) {
    return false;
  }
  return isComponentOwnedMemberEntityHandle(handle) && isComponentMemberTypeReassignable(handle.type);
}

/** True when the annotation's parent is a level (not a room or other host). */
export function docAnnotationIsLevelHosted(doc: Doc, annotation: { parentId?: string }): boolean {
  const { parentId } = annotation;
  return !!parentId && !!doc.levels[parentId];
}

/**
 * Check if a handle is a level-hosted entity that may be reassigned to a level in a component
 */
export function isLevelHostedEntityHandle(
  doc: Doc,
  handle: DocEntityHandle | null | undefined,
): handle is LevelHostedEntityHandle {
  if (!handle || !isLevelHostedEntityType(handle.type)) {
    return false;
  }
  switch (handle.type) {
    case "ANNOTATION":
      return docAnnotationIsLevelHosted(doc, doc.annotation[handle.id]);
    case "STAIR_SYSTEM":
    case "RAILING":
    case "FOUNDATION_SYSTEM":
    case "FLOOR_SYSTEM":
      return true;
    default:
      assertUnreachable(handle.type);
  }
}

/**
 * Type guard to check if a DocEntityType is a ComponentMemberEntityType.
 */
export function isComponentMemberEntityType(type: DocEntityType): type is ComponentMemberEntityType {
  return componentMemberEntityTypes.includes(type as ComponentMemberEntityType);
}

/**
 * Type guard to check if a DocEntityHandle is a ComponentMemberEntityHandle.
 */
export function isComponentMemberEntityHandle(
  handle: DocEntityHandle | null | undefined,
): handle is ComponentMemberEntityHandle {
  if (!handle) {
    return false;
  }
  return isComponentMemberEntityType(handle.type);
}

/**
 * Gets the component that exclusively owns an entity via componentMembers, if any.
 */
export function docEntityGetComponent(
  doc: Pick<Doc, "componentMembers">,
  handle: DocEntityHandle,
): ComponentHandle | undefined {
  if (!isComponentOwnedMemberEntityHandle(handle)) {
    return undefined;
  }

  const hash = docEntityHandleHash(handle);
  const componentId = doc.componentMembers[hash];
  if (!componentId) {
    return undefined;
  }

  return { type: "COMPONENT", id: componentId };
}

/**
 * True when the entity appears as belonging to a component in the read model.
 */
export function docEntityBelongsToComponent(
  doc: Pick<Doc, "componentMembers" | "componentAssociations">,
  handle: ComponentMemberEntityHandle,
): boolean {
  if (isComponentAssociatedEntityHandle(handle)) {
    return docGetComponentIdsForAssociatedEntity(doc, handle).length > 0;
  }
  return docEntityGetComponent(doc, handle) !== undefined;
}

/**
 * Returns the component that hosts an entity by walking up its host chain (e.g. an annotation
 * hosted on a room or level that is a component member), or undefined.
 */
export function findHostComponent(doc: Doc, handle: DocEntityHandle): ComponentHandle | undefined {
  let ancestor: DocEntityHandle | undefined = docEntityGetParent(doc, handle);
  while (ancestor) {
    const component = docEntityGetComponent(doc, ancestor);
    if (component) {
      return component;
    }
    ancestor = docEntityGetParent(doc, ancestor);
  }
  return undefined;
}

/**
 * Returns the component this entity or an ancestor of it is a member of.
 */
export function docEntityGetComponentOrHost(doc: Doc, handle: DocEntityHandle): ComponentHandle | undefined {
  return docEntityGetComponent(doc, handle) ?? findHostComponent(doc, handle);
}

/**
 * Returns the component an entity belongs to: directly as a component member, or — for annotations,
 * which are never direct members — the component that hosts it via its host chain.
 */
export function docEntityGetComponentOrAnnotationHost(doc: Doc, handle: DocEntityHandle): ComponentHandle | undefined {
  const direct = docEntityGetComponent(doc, handle);
  if (direct) {
    return direct;
  }
  if (handle.type !== "ANNOTATION") {
    return undefined;
  }
  return findHostComponent(doc, handle);
}
